import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getName } from 'country-list';
import { useLicenseGate } from '../../../hooks/useLicenseGate';
import Button from '../../shared/TailwindComponents/Button';
import { DashboardUrgentMatter, DashboardCollaborator } from '../../../store/reducer/DashboardReducer';
import Badge from '../../shared/TailwindComponents/Badge';
import Flag from '../../shared/TailwindComponents/Flag';
import ProfileImage from '../../Header/ProfileImage';
import { getJurisdictionDisplay } from '../../Matters/Matters';
import { formatDateTime } from '../../../utils/functions';
import {
  PiBriefcaseLight,
  PiArrowRightLight,
  PiPlusLight,
  PiClockLight,
} from 'react-icons/pi';

interface RecentMattersProps {
  matters: DashboardUrgentMatter[];
  loading?: boolean;
}

const formatUpdatedAt = (updatedAt: string | null): string => {
  if (!updatedAt) return '';
  const date = new Date(updatedAt);
  const now = new Date();
  const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffSec < 60) return 'Updated just now';
  if (diffSec < 3600) return `Updated ${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `Updated ${Math.floor(diffSec / 3600)}h ago`;
  const diffDays = Math.floor(diffSec / 86400);
  return `Updated ${diffDays}d ago`;
};

const getStatusColor = (status: string): 'blue' | 'green' | 'yellow' | 'red' | 'gray' => {
  switch (status?.toLowerCase()) {
    case 'active':   return 'blue';
    case 'closed':   return 'gray';
    case 'pending':  return 'yellow';
    case 'resolved': return 'green';
    default:         return 'gray';
  }
};

const capitalize = (str: string): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const PRIORITY_CONFIG: Record<string, { dot: string; label: string }> = {
  low:    { dot: 'bg-gray-400',    label: 'Low' },
  medium: { dot: 'bg-amber-400',   label: 'Medium' },
  high:   { dot: 'bg-orange-500',  label: 'High' },
  urgent: { dot: 'bg-red-500',     label: 'Urgent' },
};

const AvatarStack: React.FC<{ collaborators: DashboardCollaborator[]; total: number }> = ({
  collaborators,
  total,
}) => {
  const shown = collaborators.slice(0, 4);
  const overflow = total - shown.length;
  if (shown.length === 0) return null;
  return (
    <div className="flex items-center -space-x-1.5">
      {shown.map((c, i) => (
        <div
          key={i}
          className="border-2 border-white dark:border-gray-800 rounded-full flex-shrink-0"
          title={c.name}
        >
          <ProfileImage
            profilePicture={c.profile_picture || null}
            email={c.email || ''}
            initials={c.initials || '??'}
            name={c.name || ''}
            size="w-6 h-6"
          />
        </div>
      ))}
      {overflow > 0 && (
        <div className="w-6 h-6 rounded-full border-2 border-white dark:border-gray-800
          bg-gray-100 dark:bg-gray-700 flex items-center justify-center
          text-[9px] text-gray-500 dark:text-gray-400">
          +{overflow}
        </div>
      )}
    </div>
  );
};

const MatterCard: React.FC<{ matter: DashboardUrgentMatter; onClick: () => void }> = ({
  matter,
  onClick,
}) => {
  const jurisdictionInfo = getJurisdictionDisplay(matter.jurisdiction);
  const jurisdictionName = jurisdictionInfo
    ? (getName(jurisdictionInfo.countryCode) || matter.jurisdiction)
    : matter.jurisdiction;

  return (
    <div
      onClick={onClick}
      className="group relative flex-shrink-0 w-72 bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80
        p-4 cursor-pointer hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600
        transition-all duration-200 flex flex-col gap-3 snap-start"
    >
      <div className="flex items-start gap-2.5">
        <PiBriefcaseLight className="w-4 h-4 text-gray-600 dark:text-gray-400 flex-shrink-0 mt-0.5" />
        <h3 className="flex-1 text-sm text-gray-900 dark:text-gray-100 leading-snug line-clamp-2">
          {matter.title}
        </h3>
        <PiArrowRightLight className="flex-shrink-0 w-3.5 h-3.5 text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity mt-0.5" />
      </div>

      {jurisdictionName && (
        <div className="flex items-center gap-1.5 -mt-1">
          {jurisdictionInfo && (
            <Flag countryCode={jurisdictionInfo.countryCode} size="sm" className="rounded" />
          )}
          <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{jurisdictionName}</p>
        </div>
      )}

      {(matter.status || matter.priority) && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {matter.status && (
            <Badge color={getStatusColor(matter.status)}>
              {capitalize(matter.status)}
            </Badge>
          )}
          {matter.priority && (() => {
            const prio = PRIORITY_CONFIG[matter.priority] || PRIORITY_CONFIG.medium;
            return (
              <div className="flex items-center gap-1">
                <span className={`w-1.5 h-1.5 rounded-full ${prio.dot}`} />
                <span className="text-[11px] text-gray-400 dark:text-gray-500">{prio.label}</span>
              </div>
            );
          })()}
        </div>
      )}

      <div className="mt-auto flex items-center justify-between pt-1 border-t border-gray-100 dark:border-gray-700/60">
        <AvatarStack collaborators={matter.collaborators ?? []} total={matter.collaborator_count ?? 0} />
        {matter.updated_at && (
          <div className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
            <PiClockLight className="w-3 h-3 flex-shrink-0" />
            <span>{formatUpdatedAt(matter.updated_at)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const NewMatterCard: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const { t } = useTranslation();
  return (
    <div
      onClick={onClick}
      className="group flex-shrink-0 w-72 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700
        cursor-pointer snap-start min-h-[140px]
        hover:border-blue-300 dark:hover:border-blue-600
        hover:bg-blue-50/40 dark:hover:bg-blue-900/10
        transition-all duration-200
        flex flex-col items-center justify-center gap-2"
    >
      <div className="inline-flex items-center justify-center w-10 h-10 rounded-full
        bg-gray-100 dark:bg-gray-700
        group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30
        transition-colors">
        <PiPlusLight className="w-5 h-5 text-gray-400 dark:text-gray-500 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" />
      </div>
      <span className="text-xs text-gray-400 dark:text-gray-500 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
        {t('New Matter')}
      </span>
    </div>
  );
};

const SkeletonCard: React.FC = () => (
  <div className="flex-shrink-0 w-72 bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80 p-4 flex flex-col gap-3 snap-start">
    <div className="flex items-start gap-2.5">
      <div className="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0 animate-fadePulse" />
      <div className="flex-1 space-y-1.5">
        <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded animate-fadePulse w-4/5" />
        <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded animate-fadePulse w-3/5" />
      </div>
    </div>
    <div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded-full animate-fadePulse" />
    <div className="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-fadePulse mt-auto" />
  </div>
);

const RecentMatters: React.FC<RecentMattersProps> = ({ matters, loading }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { hasSectionAccess } = useLicenseGate();

  if (!hasSectionAccess('matters')) return null;

  const capped = matters.slice(0, 8);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3.5 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2.5">
          <PiBriefcaseLight className="w-4 h-4 text-gray-800 dark:text-gray-300" />
          <h3 className="text-sm text-gray-900 dark:text-gray-100">
            {t('Matters Recently Worked On')}
          </h3>
          {capped.length > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
              {capped.length}
            </span>
          )}
        </div>
        <Button variant="outline" size="small" onClick={() => history.push('/matters')}>
          {t('View All')}
        </Button>
      </div>

      <div className="px-5 pt-4 bg-gray-50 dark:bg-gray-900/30">
        {loading ? (
          <div className="flex gap-3 overflow-x-auto custom-scrollbar-main pb-3 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-700">
            {[0, 1, 2, 3].map(i => <SkeletonCard key={i} />)}
          </div>
        ) : capped.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 mb-3">
              <PiBriefcaseLight className="w-6 h-6 text-blue-400 dark:text-blue-500" />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('No recent matters')}
            </p>
          </div>
        ) : (
          <div className="flex gap-3 overflow-x-auto custom-scrollbar-main pb-3 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-700">
            {capped.map((matter) => (
              <MatterCard
                key={matter.uuid}
                matter={matter}
                onClick={() => history.push(`/matters/${matter.uuid}`)}
              />
            ))}
            <NewMatterCard onClick={() => history.push('/matters?add=true')} />
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentMatters;
